/**
 * Credit Balance Widget — displays credit pool status on the dashboard.
 *
 * Shows: gauge (green/yellow/red), "X of Y credits used", partner usage
 * for Partner Users, and aggregate for Org Admins.
 */

import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import type { UserRole } from '../../types/roles';

interface CreditBalance {
  rollover_credits: number;
  tier_credits: number;
  purchased_credits: number;
  reserved_credits: number;
  total_available: number;
}

interface PartnerUsageInfo {
  partition_id?: string;
  partition_name?: string;
  credits_used_this_quarter: number;
  soft_cap: number | null;
  enforcement: string | null;
  campaign_usage?: Array<{
    campaign_id: string | null;
    campaign_name: string;
    credits_used: number;
  }>;
}

interface OrgUsageSummary {
  total_credits_used?: number;
  window_start?: string;
  window_end?: string;
}

export const CreditBalanceWidget: React.FC = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [partnerUsage, setPartnerUsage] = useState<PartnerUsageInfo[]>([]);
  const [orgUsage, setOrgUsage] = useState<OrgUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const role = user?.role as UserRole | undefined;
  const isPartnerUser = role === 'partner_user';
  const isAgencyOperator = role === 'agency_operator';
  const isPartnerScoped = isPartnerUser || isAgencyOperator;
  const isOrgAdmin = role === 'org_admin' || role === 'super_admin';

  useEffect(() => {
    const fetchWidgetData = async () => {
      try {
        if (isOrgAdmin) {
          const [orgBalance, usage] = await Promise.all([
            apiFetch('/api/v1/credits/balance'),
            apiFetch('/api/v1/credits/usage').catch(() => null),
          ]);
          setBalance(orgBalance);
          setOrgUsage(usage as OrgUsageSummary | null);
          setPartnerUsage([]);
        } else if (isPartnerUser && user?.partition_id) {
          const usage = await apiFetch(`/api/v1/credits/usage/partner/${user.partition_id}`);
          setPartnerUsage([{
            partition_id: user.partition_id,
            partition_name: 'Your Partition',
            ...usage,
          }]);
          setBalance(null);
          setOrgUsage(null);
        } else if (isAgencyOperator) {
          const assignedPartitions = user?.assigned_partition_ids || [];
          if (assignedPartitions.length === 0) {
            setPartnerUsage([]);
            setBalance(null);
            setOrgUsage(null);
          } else {
            const usageResults = await Promise.allSettled(
              assignedPartitions.map((partitionId) => apiFetch(`/api/v1/credits/usage/partner/${partitionId}`))
            );
            const usageRows = usageResults
              .map((result, index) => {
                if (result.status !== 'fulfilled') return null;
                const partitionId = assignedPartitions[index];
                return {
                  partition_id: partitionId,
                  partition_name: `Partition ${partitionId.slice(0, 8)}`,
                  ...result.value,
                } as PartnerUsageInfo;
              })
              .filter((row): row is PartnerUsageInfo => row !== null);
            setPartnerUsage(usageRows);
            setBalance(null);
            setOrgUsage(null);
          }
        } else {
          setBalance(null);
          setPartnerUsage([]);
          setOrgUsage(null);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load credit balance');
      } finally {
        setLoading(false);
      }
    };
    fetchWidgetData();
  }, [
    isOrgAdmin,
    isPartnerUser,
    isAgencyOperator,
    user?.partition_id,
    user?.assigned_partition_ids,
  ]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
        <div className="h-8 bg-gray-200 rounded w-1/2" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border p-4 text-sm text-gray-500">
        Credit balance unavailable
      </div>
    );
  }

  if (isPartnerScoped) {
    const totalUsed = partnerUsage.reduce((sum, row) => sum + row.credits_used_this_quarter, 0);
    const totalCap = partnerUsage.reduce((sum, row) => sum + (row.soft_cap || 0), 0);
    const hasCaps = partnerUsage.some((row) => row.soft_cap !== null);
    const pctUsed = hasCaps && totalCap > 0 ? Math.round((totalUsed / totalCap) * 100) : 0;

    const gaugeColor =
      pctUsed >= 100 ? 'bg-red-500' :
      pctUsed >= 80 ? 'bg-amber-500' :
      'bg-green-500';

    return (
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">
            {isAgencyOperator ? 'Assigned Partner Usage' : 'Your Partner Usage'}
          </h3>
          {hasCaps && (
            <span className="text-xs text-gray-500">{pctUsed}% of allocation</span>
          )}
        </div>

        {hasCaps && (
          <>
            <div className="w-full h-2 bg-gray-200 rounded-full mb-3">
              <div
                className={`h-2 rounded-full transition-all ${gaugeColor}`}
                style={{ width: `${Math.min(pctUsed, 100)}%` }}
              />
            </div>
            <div className="text-sm text-gray-700 mb-3">
              <span className="font-semibold">{totalUsed}</span> used of{' '}
              <span className="font-semibold">{totalCap}</span> allocated credits
            </div>
          </>
        )}

        <div className="space-y-2">
          {partnerUsage.length === 0 ? (
            <div className="text-xs text-gray-500">No assigned partitions.</div>
          ) : (
            partnerUsage.map((row) => {
              const rowPct = row.soft_cap ? Math.round((row.credits_used_this_quarter / row.soft_cap) * 100) : null;
              return (
                <div key={row.partition_id || row.partition_name} className="text-xs border rounded p-2 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">
                      {row.partition_name || row.partition_id || 'Partition'}
                    </span>
                    <span className="text-gray-600">
                      {row.credits_used_this_quarter}
                      {row.soft_cap !== null ? ` / ${row.soft_cap}` : ''} credits
                    </span>
                  </div>
                  {rowPct !== null && (
                    <div className={`mt-1 ${rowPct > 100 ? 'text-red-600' : rowPct >= 80 ? 'text-amber-600' : 'text-gray-500'}`}>
                      {rowPct}% of cap
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="bg-white rounded-lg border p-4 text-sm text-gray-500">
        Credit balance unavailable
      </div>
    );
  }

  const available = balance.total_available;
  const usedThisCycle = Number(orgUsage?.total_credits_used || 0);
  const cyclePoolReference = usedThisCycle + available;
  const pctUsed = cyclePoolReference > 0
    ? Math.round((usedThisCycle / cyclePoolReference) * 100)
    : 0;

  const gaugeColor =
    pctUsed >= 80 ? 'bg-red-500' :
    pctUsed >= 50 ? 'bg-yellow-500' :
    'bg-green-500';

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Credit Balance</h3>
        <span className="text-xs text-gray-500">{pctUsed}% used</span>
      </div>

      {/* Gauge bar */}
      <div className="w-full h-2 bg-gray-200 rounded-full mb-3">
        <div
          className={`h-2 rounded-full transition-all ${gaugeColor}`}
          style={{ width: `${Math.min(pctUsed, 100)}%` }}
        />
      </div>

      <div className="flex justify-between text-sm">
        <span className="text-gray-600">
          <span className="font-semibold text-gray-900">{available}</span> credits available
        </span>
        <span className="text-gray-500">{usedThisCycle} used this cycle</span>
      </div>

      {/* Sub-balance breakdown */}
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-gray-500">
        <div>
          <div className="font-medium text-gray-700">{balance.tier_credits}</div>
          <div>Quarterly</div>
        </div>
        <div>
          <div className="font-medium text-gray-700">{balance.rollover_credits}</div>
          <div>Rollover</div>
        </div>
        <div>
          <div className="font-medium text-gray-700">{balance.purchased_credits}</div>
          <div>Purchased</div>
        </div>
      </div>

      {balance.reserved_credits > 0 && (
        <div className="mt-2 text-xs text-amber-600">
          {balance.reserved_credits} credits currently reserved
        </div>
      )}

      {/* Org Admin sees full pool-level balances and reserved amounts. */}
    </div>
  );
};

export default CreditBalanceWidget;
